import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { api, ApiError } from '../lib/api';
import type { ClientFrame, MessageBody, ServerFrame } from '@ts/schema/types';
import { MentionTextarea, renderWithMentions } from '../components/MentionTextarea';
import { ScreenHeader } from '../components/ScreenHeader';

interface ChatMessage {
  id: string;
  authorId: string;
  kind: string;
  body: MessageBody;
  createdAt: number;
  reactions?: { memberId: string; kind: string }[];
  /** True while the optimistic local row is awaiting server confirmation. */
  pending?: boolean;
  /** True if the REST send failed; lets the bubble surface an error glyph. */
  failed?: boolean;
}

interface ServerSendResponse {
  message: {
    id: string;
    authorId: string;
    kind: string;
    body: MessageBody;
    createdAt: number;
  };
}

type HistoryState =
  | { kind: 'loading' }
  | { kind: 'error'; message: string }
  | { kind: 'ready' };

type ShareSheet = { open: false } | { open: true; mode: 'menu' | 'photos' };

export default function ChatPage() {
  const auth = useAuth();
  const me = auth.state === 'signed-in' ? auth.me : null;
  const [history, setHistory] = useState<HistoryState>({ kind: 'loading' });
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [draft, setDraft] = useState('');
  const [draftMentions, setDraftMentions] = useState<string[]>([]);
  const [sheet, setSheet] = useState<ShareSheet>({ open: false });
  const wsRef = useRef<WebSocket | null>(null);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [actionsForId, setActionsForId] = useState<string | null>(null);
  const composerRef = useRef<HTMLTextAreaElement | null>(null);

  async function deleteMessage(id: string) {
    // Optimistic local removal.
    setMessages((prev) => prev.filter((m) => m.id !== id));
    setConfirmDeleteId(null);
    try {
      await api.delete(`/chat/messages/${id}`);
    } catch (e) {
      console.error('delete failed:', e);
      // The user already saw it vanish; surfacing an error toast would
      // require building a toast system. For v1 we accept a silent
      // local rollback would surprise the user more.
    }
  }

  // Pull recent history. Runs on mount and again on every reconnect /
  // return-to-foreground, since the live socket only delivers frames
  // while connected — anything sent while it was asleep is filled in
  // here. Preserves any in-flight optimistic rows the server hasn't
  // echoed back yet.
  const loadHistory = useCallback(async () => {
    try {
      const r = await api.get<{ messages: ChatMessage[] }>('/chat/history');
      const server = r.messages.slice().reverse();
      const serverIds = new Set(server.map((m) => m.id));
      setMessages((prev) => [
        ...server,
        ...prev.filter((m) => (m.pending || m.failed) && !serverIds.has(m.id)),
      ]);
      setHistory({ kind: 'ready' });
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : 'Could not load messages.';
      // Don't clobber an already-loaded view if a resume refetch fails.
      setHistory((h) => (h.kind === 'ready' ? h : { kind: 'error', message: msg }));
    }
  }, []);

  // Live WebSocket with reconnection. iOS home-screen PWAs silently
  // close sockets when backgrounded; without this the chat would stop
  // updating until a full reload (sends still worked via the REST
  // fallback — which is exactly how the bug presented). We reconnect on
  // close (capped backoff), on return to the foreground, and on network
  // recovery, refetching history each time to catch up. A periodic ping
  // keeps an idle socket from being culled.
  useEffect(() => {
    if (!me) return;
    void loadHistory();

    const proto = window.location.protocol === 'https:' ? 'wss' : 'ws';
    let closedByUs = false;
    let reconnectTimer: ReturnType<typeof setTimeout> | undefined;
    let pingTimer: ReturnType<typeof setInterval> | undefined;
    let attempts = 0;
    let firstOpen = true;

    function scheduleReconnect() {
      if (closedByUs || reconnectTimer) return;
      attempts += 1;
      const delay = Math.min(1000 * 2 ** (attempts - 1), 15_000);
      reconnectTimer = setTimeout(() => {
        reconnectTimer = undefined;
        connect();
      }, delay);
    }

    function connect() {
      if (closedByUs) return;
      const existing = wsRef.current;
      // Already connecting/open — don't stack sockets.
      if (existing && existing.readyState <= WebSocket.OPEN) return;

      let ws: WebSocket;
      try {
        ws = new WebSocket(`${proto}://${window.location.host}/api/chat/connect`);
      } catch {
        scheduleReconnect();
        return;
      }
      wsRef.current = ws;

      ws.addEventListener('open', () => {
        attempts = 0;
        if (pingTimer) clearInterval(pingTimer);
        pingTimer = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) {
            try {
              ws.send(JSON.stringify({ type: 'ping' } satisfies ClientFrame));
            } catch {
              /* socket gone; close handler will reconnect */
            }
          }
        }, 25_000);
        // On a reconnect (not the initial open), backfill missed messages.
        if (!firstOpen) void loadHistory();
        firstOpen = false;
      });

      ws.addEventListener('message', (e) => {
        try {
          const frame = JSON.parse(String(e.data)) as ServerFrame;
          if (frame.type === 'message') {
            setMessages((prev) => mergeServerMessage(prev, frame.message as ChatMessage));
          } else if (frame.type === 'reaction') {
            setMessages((prev) => applyReaction(prev, frame));
          } else if (frame.type === 'message_deleted') {
            setMessages((prev) => prev.filter((m) => m.id !== frame.messageId));
          }
        } catch {
          /* ignore malformed frame */
        }
      });

      ws.addEventListener('close', () => {
        if (pingTimer) clearInterval(pingTimer);
        pingTimer = undefined;
        if (!closedByUs) scheduleReconnect();
      });
      ws.addEventListener('error', () => {
        /* a close event follows; reconnection is handled there */
      });
    }

    // Return-to-foreground / network recovery: reconnect a dead socket
    // immediately (resetting backoff), or — for an iOS "zombie" socket
    // that still reports OPEN but is really dead — just refetch.
    function resume() {
      if (document.visibilityState !== 'visible') return;
      const ws = wsRef.current;
      const dead =
        !ws || ws.readyState === WebSocket.CLOSING || ws.readyState === WebSocket.CLOSED;
      if (dead) {
        attempts = 0;
        if (reconnectTimer) {
          clearTimeout(reconnectTimer);
          reconnectTimer = undefined;
        }
        connect();
      } else {
        void loadHistory();
      }
    }

    connect();
    document.addEventListener('visibilitychange', resume);
    window.addEventListener('online', resume);
    window.addEventListener('focus', resume);

    return () => {
      closedByUs = true;
      if (reconnectTimer) clearTimeout(reconnectTimer);
      if (pingTimer) clearInterval(pingTimer);
      document.removeEventListener('visibilitychange', resume);
      window.removeEventListener('online', resume);
      window.removeEventListener('focus', resume);
      try {
        wsRef.current?.close();
      } catch {
        /* noop */
      }
      wsRef.current = null;
    };
  }, [me?.member.id, loadHistory]);

  // Auto-scroll on new message.
  useEffect(() => {
    scrollRef.current?.scrollTo({
      top: scrollRef.current.scrollHeight,
      behavior: 'smooth',
    });
  }, [messages.length]);

  /** Send a frame. Prefers WebSocket; falls back to REST. */
  async function publish(body: MessageBody) {
    if (!me) return;
    const tempId = `tmp_${crypto.randomUUID?.() ?? Math.random().toString(36).slice(2)}`;
    const optimistic: ChatMessage = {
      id: tempId,
      authorId: me.member.id,
      kind: body.kind,
      body,
      createdAt: Date.now(),
      reactions: [],
      pending: true,
    };
    setMessages((prev) => [...prev, optimistic]);

    const ws = wsRef.current;
    if (ws && ws.readyState === WebSocket.OPEN) {
      const frame: ClientFrame = { type: 'send', body };
      try {
        ws.send(JSON.stringify(frame));
        // WS broadcast will replace the optimistic row when 'message'
        // arrives back (matched by author+createdAt closest); meanwhile
        // we leave `pending: true` and clear it via mergeServerMessage.
        return;
      } catch {
        // Socket might be dead despite OPEN — fall through to REST.
      }
    }

    try {
      const res = await api.post<ServerSendResponse>('/chat/send', {
        body,
      });
      setMessages((prev) =>
        prev.map((m) =>
          m.id === tempId ? { ...res.message, body, reactions: [], pending: false } : m,
        ),
      );
    } catch (e) {
      // Mark the optimistic row as failed so the user sees something went wrong.
      const msg = e instanceof ApiError ? e.message : 'Send failed';
      console.error('chat send failed:', msg);
      setMessages((prev) =>
        prev.map((m) => (m.id === tempId ? { ...m, pending: false, failed: true } : m)),
      );
    }
  }

  function sendText() {
    const text = draft.trim();
    if (!text) return;
    const mentions = draftMentions;
    setDraft('');
    setDraftMentions([]);
    void publish({ kind: 'text', text, mentions });
  }

  // Toggle a reaction with REST, optimistic. `kind` defaults to praying
  // to preserve the legacy double-tap behavior; the long-press menu's
  // Love action passes 'heart' for a second reaction kind.
  async function toggleReaction(
    messageId: string,
    kind: 'praying' | 'heart' = 'praying',
  ) {
    if (!me) return;
    setMessages((prev) =>
      prev.map((m) => {
        if (m.id !== messageId) return m;
        const has = m.reactions?.some(
          (r) => r.memberId === me.member.id && r.kind === kind,
        );
        const nextReactions = has
          ? (m.reactions ?? []).filter(
              (r) => !(r.memberId === me.member.id && r.kind === kind),
            )
          : [...(m.reactions ?? []), { memberId: me.member.id, kind }];
        return { ...m, reactions: nextReactions };
      }),
    );
    const ws = wsRef.current;
    if (ws && ws.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify({ type: 'react', messageId, reaction: kind }));
      return;
    }
    try {
      const hasNow = messages
        .find((m) => m.id === messageId)
        ?.reactions?.some((r) => r.memberId === me.member.id && r.kind === kind);
      if (hasNow) {
        await api.delete(`/chat/messages/${messageId}/react?kind=${kind}`);
      } else {
        await api.post(`/chat/messages/${messageId}/react?kind=${kind}`);
      }
    } catch (e) {
      console.error('reaction toggle failed:', e);
    }
  }

  return (
    <div
      className="fixed inset-0 flex flex-col bg-paper"
      // Bottom padding matches the TabBar's actual rendered height
      // (120px flex row + iOS safe-area inset) plus an 8px breathing
      // band — keeps the composer tight to the nav on every device.
      // A static value over-pads on non-notched and under-pads on
      // iPhone where the safe-area inset is ~34px.
      style={{
        paddingBottom: 'calc(120px + env(safe-area-inset-bottom, 0px) + 8px)',
      }}
    >
      <ScreenHeader
        title="Chat"
        right={
          <div className="flex -space-x-1.5">
            {me?.members.map((m) => (
              <Avatar key={m.id} name={m.displayName} role={m.role} />
            ))}
          </div>
        }
      />

      <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-4">
        {history.kind === 'loading' && (
          <div className="text-center text-muted text-sm py-8">Loading messages…</div>
        )}
        {history.kind === 'error' && (
          <div className="text-center text-oxblood text-sm py-8">{history.message}</div>
        )}
        {history.kind === 'ready' && messages.length === 0 && (
          <div className="text-center text-muted italic py-12">
            <p className="font-serif text-xl text-soft">No messages yet.</p>
            <p className="text-sm mt-2">Send the first one to your family.</p>
          </div>
        )}
        {messages.map((m) => (
          <MessageBubble
            key={m.id}
            msg={m}
            isMe={m.authorId === me?.member.id}
            author={
              me?.members.find((x) => x.id === m.authorId)?.displayName ?? 'Someone'
            }
            onReact={() => toggleReaction(m.id, 'praying')}
            onReactKind={(kind) => toggleReaction(m.id, kind)}
            onLongPress={
              m.id.startsWith('tmp_') ? undefined : () => setActionsForId(m.id)
            }
          />
        ))}
      </div>

      <div className="bg-card border-t border-line p-3 flex items-center gap-2">
        <button
          onClick={() => setSheet({ open: true, mode: 'menu' })}
          aria-label="Share"
          className="w-11 h-11 rounded-full border-2 border-oxblood text-oxblood flex items-center justify-center text-xl shrink-0"
        >
          +
        </button>
        <div className="flex-1 min-w-0">
          <MentionTextarea
            ref={composerRef}
            value={draft}
            onChange={(v, m) => {
              setDraft(v);
              setDraftMentions(m);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                sendText();
              }
            }}
            placeholder="Message the family…"
            rows={1}
            className="w-full px-4 py-2.5 rounded-2xl border border-line bg-paper italic text-ink resize-none"
          />
        </div>
        <button
          type="button"
          onClick={sendText}
          disabled={!draft.trim()}
          aria-label="Send message"
          className="w-11 h-11 rounded-full bg-sage text-paper flex items-center justify-center flex-none disabled:opacity-40"
        >
          <svg viewBox="0 0 24 24" className="w-5 h-5" fill="none">
            <path
              d="M4 12l16-8-6 16-3-7-7-1z"
              stroke="currentColor"
              strokeWidth={1.7}
              strokeLinejoin="round"
              strokeLinecap="round"
            />
          </svg>
        </button>
      </div>

      {sheet.open && (
        <ShareSheetOverlay
          mode={sheet.mode}
          onClose={() => setSheet({ open: false })}
          onShareVerse={async () => {
            setSheet({ open: false });
            try {
              const today = await api.get<{
                date: string;
                verse: { ref: string; body: string };
              }>('/today');
              const excerpt =
                today.verse.body.length > 220
                  ? today.verse.body.slice(0, 220).trim() + '…'
                  : today.verse.body;
              await publish({
                kind: 'shared_verse',
                date: today.date,
                ref: today.verse.ref,
                excerpt,
              });
            } catch (e) {
              console.error('share verse failed:', e);
            }
          }}
          onPickPhoto={() => setSheet({ open: true, mode: 'photos' })}
          onSelectPhoto={async (photoId) => {
            setSheet({ open: false });
            await publish({ kind: 'shared_photo', photoId });
          }}
        />
      )}

      {confirmDeleteId && (
        <ConfirmDeleteOverlay
          title="Delete this message?"
          body="It will be removed for everyone in the family."
          onCancel={() => setConfirmDeleteId(null)}
          onConfirm={() => void deleteMessage(confirmDeleteId)}
        />
      )}

      {actionsForId &&
        (() => {
          const m = messages.find((x) => x.id === actionsForId);
          if (!m) return null;
          const isAuthor = m.authorId === me?.member.id && !m.id.startsWith('tmp_');
          const authorName =
            me?.members.find((x) => x.id === m.authorId)?.displayName ?? 'Someone';
          const copyText = copyableText(m.body);
          return (
            <MessageActionsSheet
              onClose={() => setActionsForId(null)}
              onLove={() => {
                void toggleReaction(m.id, 'heart');
                setActionsForId(null);
              }}
              onReply={() => {
                // Soft reply v1: pre-fill the composer with a quoted
                // copy of the original message (so the author sees what
                // they're being replied to in-line) followed by an
                // @author mention so they're notified. Full quote-
                // threading would still want a parentMessageId column,
                // but this is enough for a 4-person family chat.
                const quote = quoteForReply(m.body);
                const prefix = quote ? `${quote}\n@${authorName} ` : `@${authorName} `;
                setDraft((d) => (d ? `${d.replace(/\s+$/, '')}\n${prefix}` : prefix));
                setActionsForId(null);
                setTimeout(() => composerRef.current?.focus(), 0);
              }}
              onCopy={
                copyText
                  ? () => {
                      void navigator.clipboard
                        ?.writeText(copyText)
                        .catch((e) => console.error('copy failed:', e));
                      setActionsForId(null);
                    }
                  : undefined
              }
              onDelete={
                isAuthor
                  ? () => {
                      setConfirmDeleteId(m.id);
                      setActionsForId(null);
                    }
                  : undefined
              }
            />
          );
        })()}
    </div>
  );
}

function ConfirmDeleteOverlay({
  title,
  body,
  onCancel,
  onConfirm,
}: {
  title: string;
  body: string;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  // Portaled to document.body so the sheet escapes the Chat page's
  // `fixed inset-0` stacking context. Otherwise the TabBar (sibling
  // of ChatPage, no z-index) paints on top of any z-50 inside chat.
  return createPortal(
    <div className="fixed inset-0 z-50 flex items-end" onClick={onCancel}>
      <div className="absolute inset-0 bg-ink/40" />
      <div
        className="relative w-full bg-paper rounded-t-2xl p-5 pb-8 z-10"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="font-serif text-2xl text-ink">{title}</h3>
        <p className="text-soft mt-1">{body}</p>
        <div className="flex gap-3 mt-5">
          <button
            onClick={onCancel}
            className="flex-1 h-12 rounded-xl border-2 border-line text-soft font-medium"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            className="flex-1 h-12 rounded-xl bg-oxblood text-paper font-medium"
          >
            Delete
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

/** Returns the plain-text representation of a message body for the
 *  Copy action, or null when the body is a pointer (photo/prayer/
 *  encouragement) that has no useful text to copy. */
function copyableText(body: MessageBody): string | null {
  if (body.kind === 'text') return body.text;
  if (body.kind === 'shared_verse') {
    return body.excerpt ? `${body.ref}\n\n${body.excerpt}` : body.ref;
  }
  return null;
}

/** Render a text-body message. Any leading "> …" lines (the format
 *  the Reply action prepends to the composer) are pulled out into a
 *  styled quote block above the reply, iMessage-style — italic, on a
 *  dimmed background tint with a left rule. Everything after is the
 *  reply itself, rendered with mention pills as usual. */
function renderTextBody(text: string, inverted: boolean) {
  const lines = text.split('\n');
  const quoteLines: string[] = [];
  let i = 0;
  while (i < lines.length && lines[i]!.startsWith('> ')) {
    quoteLines.push(lines[i]!.slice(2));
    i++;
  }
  // Drop a single blank line between quote and reply (the composer
  // inserts one to visually separate them in the textarea).
  if (i < lines.length && lines[i]!.trim() === '') i++;
  const rest = lines.slice(i).join('\n');

  return (
    <>
      {quoteLines.length > 0 && (
        <div
          className={`mb-2 pl-3 border-l-2 text-sm italic ${
            inverted ? 'border-paper/50 text-paper/80' : 'border-muted/50 text-soft'
          }`}
        >
          {quoteLines.map((line, idx) => (
            <div key={idx}>{line}</div>
          ))}
        </div>
      )}
      {rest && (
        <p className="text-base leading-relaxed whitespace-pre-wrap">
          {renderWithMentions(rest, { inverted })}
        </p>
      )}
    </>
  );
}

/** Renders a message body as one or more "> …" lines for the Reply
 *  composer. Long text is trimmed so the quote stays glanceable; the
 *  recipient can scroll the original if they want the full thing.
 *  Returns null for pointer bodies that have no useful text. */
function quoteForReply(body: MessageBody): string | null {
  let text: string | null = null;
  if (body.kind === 'text') text = body.text;
  else if (body.kind === 'shared_verse')
    text = body.excerpt ? `${body.ref} — ${body.excerpt}` : body.ref;
  if (!text) return null;
  const trimmed = text.length > 200 ? text.slice(0, 200).trimEnd() + '…' : text;
  // Prefix every line so multi-line originals stay visually quoted.
  return trimmed
    .split('\n')
    .map((line) => `> ${line}`)
    .join('\n');
}

/** Replace any optimistic temp row for the same author + ~time with the
 * server's authoritative row, so the user doesn't see duplicates. */
function mergeServerMessage(prev: ChatMessage[], incoming: ChatMessage): ChatMessage[] {
  // Replace any pending row from same author whose createdAt is within 30s.
  const replaceIdx = prev.findIndex(
    (m) =>
      m.pending &&
      m.authorId === incoming.authorId &&
      Math.abs(m.createdAt - incoming.createdAt) < 30_000 &&
      m.kind === incoming.kind,
  );
  if (replaceIdx >= 0) {
    const copy = prev.slice();
    copy[replaceIdx] = { ...incoming, pending: false };
    return copy;
  }
  // Otherwise dedupe by id, then append.
  if (prev.some((m) => m.id === incoming.id)) return prev;
  return [...prev, incoming];
}

function applyReaction(
  prev: ChatMessage[],
  frame: Extract<ServerFrame, { type: 'reaction' }>,
): ChatMessage[] {
  return prev.map((m) => {
    if (m.id !== frame.messageId) return m;
    const rest = (m.reactions ?? []).filter(
      (r) => !(r.memberId === frame.memberId && r.kind === frame.reaction),
    );
    return {
      ...m,
      reactions: frame.added
        ? [...rest, { memberId: frame.memberId, kind: frame.reaction }]
        : rest,
    };
  });
}

function Avatar({ name, role }: { name: string; role: 'parent' | 'youth' }) {
  const initial = name[0]?.toUpperCase() ?? '?';
  const color = role === 'parent' ? '#5C4F3C' : '#3E5D44';
  return (
    <div
      style={{ background: color }}
      className="w-9 h-9 rounded-full flex items-center justify-center text-paper text-sm font-medium border-2 border-paper"
    >
      {initial}
    </div>
  );
}

function MessageBubble({
  msg,
  isMe,
  author,
  onReact,
  onReactKind,
  onLongPress,
}: {
  msg: ChatMessage;
  isMe: boolean;
  author: string;
  onReact: () => void;
  onReactKind: (kind: 'praying' | 'heart') => void;
  onLongPress?: () => void;
}) {
  // Long-press triggers the actions sheet. Implementation notes:
  //  - Pointer events only (touch + mouse + pen unified). Listening
  //    for both onTouchStart AND onPointerDown was the source of the
  //    "delete fires while scrolling" bug — pointer events fire
  //    synchronously on the touch start AND a synthesized mouse event
  //    can fire as the browser hands the gesture off to the scroller,
  //    racing the timer cleanup.
  //  - Movement threshold: if the finger drifts more than 10px in any
  //    direction, treat the gesture as a scroll and cancel the timer.
  //    Scrolling a long thread otherwise spuriously trips long-press.
  //  - onPointerCancel: fires when the browser steals the gesture
  //    (e.g. native scroll takes over). Must cancel the timer.
  const pressTimerRef = useRef<number | null>(null);
  const startPosRef = useRef<{ x: number; y: number } | null>(null);

  function startPress(e: React.PointerEvent<HTMLDivElement>) {
    if (!onLongPress) return;
    cancelPress();
    startPosRef.current = { x: e.clientX, y: e.clientY };
    pressTimerRef.current = window.setTimeout(() => {
      onLongPress();
      pressTimerRef.current = null;
    }, 550);
  }
  function maybeCancelOnMove(e: React.PointerEvent<HTMLDivElement>) {
    if (!startPosRef.current || pressTimerRef.current == null) return;
    const dx = Math.abs(e.clientX - startPosRef.current.x);
    const dy = Math.abs(e.clientY - startPosRef.current.y);
    if (dx > 10 || dy > 10) cancelPress();
  }
  function cancelPress() {
    if (pressTimerRef.current != null) {
      window.clearTimeout(pressTimerRef.current);
      pressTimerRef.current = null;
    }
    startPosRef.current = null;
  }

  const time = new Date(msg.createdAt).toLocaleTimeString([], {
    hour: 'numeric',
    minute: '2-digit',
  });
  const bubbleStyle = isMe
    ? 'ml-auto bg-oxblood text-paper'
    : 'bg-card border border-line text-ink';

  // Group reactions by kind so we can show separate chips for
  // praying (the legacy double-tap reaction) and heart (added with
  // the long-press Love action).
  const counts = (msg.reactions ?? []).reduce<Record<string, number>>((acc, r) => {
    acc[r.kind] = (acc[r.kind] ?? 0) + 1;
    return acc;
  }, {});

  return (
    <div className={`max-w-[80%] ${isMe ? 'ml-auto' : ''}`}>
      <div
        className={`rounded-2xl px-4 py-3 select-none ${bubbleStyle} ${
          msg.pending ? 'opacity-70' : ''
        }`}
        // Suppress iOS Safari's native long-press behaviors so our 550ms
        // pointer-down timer owns the gesture: no callout (Copy/Look Up),
        // no text selection drag, no gray tap-highlight flash. Copy is
        // exposed in the action sheet instead.
        style={{
          WebkitTouchCallout: 'none',
          WebkitUserSelect: 'none',
          WebkitTapHighlightColor: 'transparent',
        }}
        onDoubleClick={onReact}
        onPointerDown={startPress}
        onPointerMove={maybeCancelOnMove}
        onPointerUp={cancelPress}
        onPointerCancel={cancelPress}
        onPointerLeave={cancelPress}
      >
        <BubbleContent body={msg.body} inverted={isMe} />
      </div>
      <div
        className={`text-xs text-muted mt-1 px-1 flex items-center gap-2 flex-wrap ${
          isMe ? 'justify-end' : ''
        }`}
      >
        <span>
          {isMe ? time : `${author} · ${time}`}
          {msg.pending && ' · sending…'}
          {msg.failed && <span className="text-oxblood font-medium"> · failed</span>}
        </span>
        {(counts.praying ?? 0) > 0 && (
          <button
            onClick={() => onReactKind('praying')}
            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-card border border-line text-sage"
          >
            <span className="w-2 h-2 rounded-full bg-sage" /> {counts.praying} praying
          </button>
        )}
        {(counts.heart ?? 0) > 0 && (
          <button
            onClick={() => onReactKind('heart')}
            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-card border border-line text-oxblood"
          >
            <svg viewBox="0 0 24 24" className="w-3 h-3" fill="currentColor">
              <path d="M12 21s-7-4.35-9-9.27C1.5 8.5 3.5 5 7 5c2 0 3.5 1.16 5 3 1.5-1.84 3-3 5-3 3.5 0 5.5 3.5 4 6.73C19 16.65 12 21 12 21z" />
            </svg>
            {counts.heart}
          </button>
        )}
      </div>
    </div>
  );
}

/**
 * Long-press action sheet for a message. Portal-rendered to escape
 * the Chat page's `fixed inset-0` stacking context (same fix as
 * ShareSheetOverlay / ConfirmDeleteOverlay).
 */
function MessageActionsSheet({
  onClose,
  onLove,
  onReply,
  onCopy,
  onDelete,
}: {
  onClose: () => void;
  onLove: () => void;
  onReply: () => void;
  /** Defined only when the message body has copyable text (text or
   *  shared_verse). Photos/prayer/encouragement pointers are skipped. */
  onCopy?: () => void;
  /** Defined only when the caller is the author. */
  onDelete?: () => void;
}) {
  return createPortal(
    <div className="fixed inset-0 z-50 flex items-end" onClick={onClose}>
      <div className="absolute inset-0 bg-ink/40" aria-hidden />
      <div
        className="relative w-full bg-paper rounded-t-2xl p-3 z-10"
        onClick={(e) => e.stopPropagation()}
        style={{ paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 16px)' }}
      >
        <div className="w-12 h-1 bg-muted/40 rounded-full mx-auto mb-3" />
        <button
          onClick={onLove}
          className="w-full flex items-center gap-3 px-3 py-3 rounded-xl hover:bg-line/40 text-left"
        >
          <span className="w-9 h-9 rounded-full bg-oxblood/10 text-oxblood flex items-center justify-center">
            <svg viewBox="0 0 24 24" className="w-5 h-5" fill="currentColor">
              <path d="M12 21s-7-4.35-9-9.27C1.5 8.5 3.5 5 7 5c2 0 3.5 1.16 5 3 1.5-1.84 3-3 5-3 3.5 0 5.5 3.5 4 6.73C19 16.65 12 21 12 21z" />
            </svg>
          </span>
          <span className="text-ink">Love</span>
        </button>
        <button
          onClick={onReply}
          className="w-full flex items-center gap-3 px-3 py-3 rounded-xl hover:bg-line/40 text-left"
        >
          <span className="w-9 h-9 rounded-full bg-sage/15 text-sage flex items-center justify-center">
            <svg viewBox="0 0 24 24" className="w-5 h-5" fill="none">
              <path
                d="M10 9V5L3 12l7 7v-4c4 0 8 1 11 4-1-6-5-10-11-10z"
                stroke="currentColor"
                strokeWidth={1.7}
                strokeLinejoin="round"
                strokeLinecap="round"
              />
            </svg>
          </span>
          <span className="text-ink">Reply</span>
        </button>
        {onCopy && (
          <button
            onClick={onCopy}
            className="w-full flex items-center gap-3 px-3 py-3 rounded-xl hover:bg-line/40 text-left"
          >
            <span className="w-9 h-9 rounded-full bg-sage/15 text-sage flex items-center justify-center">
              <svg viewBox="0 0 24 24" className="w-5 h-5" fill="none">
                <path
                  d="M9 3h9a2 2 0 0 1 2 2v11M6 7h9a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V9a2 2 0 0 1 2-2z"
                  stroke="currentColor"
                  strokeWidth={1.7}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </span>
            <span className="text-ink">Copy</span>
          </button>
        )}
        {onDelete && (
          <button
            onClick={onDelete}
            className="w-full flex items-center gap-3 px-3 py-3 rounded-xl hover:bg-line/40 text-left"
          >
            <span className="w-9 h-9 rounded-full bg-muted/15 text-soft flex items-center justify-center">
              <svg viewBox="0 0 24 24" className="w-5 h-5" fill="none">
                <path
                  d="M5 7h14M9 7V5a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2M7 7l1 13a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1l1-13"
                  stroke="currentColor"
                  strokeWidth={1.7}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </span>
            <span className="text-ink">Delete</span>
          </button>
        )}
        <button onClick={onClose} className="w-full mt-1 px-3 py-3 text-soft text-sm">
          Cancel
        </button>
      </div>
    </div>,
    document.body,
  );
}

function BubbleContent({ body, inverted }: { body: MessageBody; inverted: boolean }) {
  if (body.kind === 'text') return renderTextBody(body.text, inverted);
  if (body.kind === 'shared_verse') {
    return (
      <div>
        <div className="kicker text-sage">Shared verse</div>
        <div className="font-serif italic text-base mt-1">{body.ref}</div>
        {body.excerpt && (
          <p className="font-serif italic text-sm opacity-90 mt-1">{body.excerpt}</p>
        )}
      </div>
    );
  }
  if (body.kind === 'shared_photo') {
    // Stable per-photo path served by the Worker with immutable cache
    // headers. Shares the browser/SW cache key with the carousel and
    // the memories grid, so any of these surfaces warms the others.
    return (
      <Link to={`/memories/${body.photoId}`} className="block -mx-1 -my-1">
        <img
          src={`/api/photos/${body.photoId}/thumb`}
          alt={body.caption ?? ''}
          className="rounded-xl max-w-full"
          loading="lazy"
          decoding="async"
        />
        {body.caption && <p className="text-sm mt-2 opacity-90">{body.caption}</p>}
      </Link>
    );
  }
  return <p className="text-sm opacity-90">[{body.kind}]</p>;
}

/** Bottom sheet for the `+` Share menu. Two actions for v1:
 *  share today's verse and share an existing photo. */
function ShareSheetOverlay({
  mode,
  onClose,
  onShareVerse,
  onPickPhoto,
  onSelectPhoto,
}: {
  mode: 'menu' | 'photos';
  onClose: () => void;
  onShareVerse: () => void;
  onPickPhoto: () => void;
  onSelectPhoto: (photoId: string) => void;
}) {
  // Portaled — see note on ConfirmDeleteOverlay.
  return createPortal(
    <div className="fixed inset-0 z-50 flex items-end" onClick={onClose}>
      <div className="absolute inset-0 bg-ink/40" aria-hidden />
      <div
        className="relative w-full max-h-[70vh] bg-paper rounded-t-2xl p-5 overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        style={{ paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 20px)' }}
      >
        <div className="w-12 h-1 bg-muted/40 rounded-full mx-auto mb-4" />
        {mode === 'menu' && (
          <div className="space-y-3">
            <button
              onClick={onShareVerse}
              className="card w-full p-4 text-left hover:bg-line/40"
            >
              <div className="kicker text-oxblood">Scripture</div>
              <div className="font-serif text-xl text-ink mt-1">Share today's verse</div>
            </button>
            <button
              onClick={onPickPhoto}
              className="card w-full p-4 text-left hover:bg-line/40"
            >
              <div className="kicker text-sage">Memory</div>
              <div className="font-serif text-xl text-ink mt-1">Share a photo</div>
            </button>
          </div>
        )}
        {mode === 'photos' && <PhotoPickerSheet onSelectPhoto={onSelectPhoto} />}
      </div>
    </div>,
    document.body,
  );
}

function PhotoPickerSheet({ onSelectPhoto }: { onSelectPhoto: (id: string) => void }) {
  const [photos, setPhotos] = useState<{ id: string; url: string }[] | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    api
      .get<{ photos: { id: string; url: string }[] }>('/photos/grid')
      .then((r) => setPhotos(r.photos))
      .catch((e: unknown) =>
        setErr(e instanceof ApiError ? e.message : 'Could not load photos'),
      );
  }, []);

  if (err) return <p className="text-oxblood text-sm py-4">{err}</p>;
  if (!photos) return <p className="text-soft text-sm py-4">Loading…</p>;
  if (photos.length === 0)
    return (
      <p className="text-muted italic py-4 text-center">
        No photos uploaded yet. Add some from Memories.
      </p>
    );

  return (
    <div>
      <h3 className="font-serif text-lg text-ink mb-3">Pick a photo</h3>
      <div className="grid grid-cols-3 gap-2">
        {photos.map((p) => (
          <button
            key={p.id}
            onClick={() => onSelectPhoto(p.id)}
            className="aspect-square w-full overflow-hidden rounded-lg bg-line"
          >
            <img
              src={`/api/photos/${p.id}/thumb`}
              alt=""
              className="w-full h-full object-cover"
              loading="lazy"
              decoding="async"
            />
          </button>
        ))}
      </div>
    </div>
  );
}
